import AttachmentResource from '@/api/resources/attachment/attachmentResource'
import attachmentTypeService from './attachmentTypeService'
import { auth } from '@/api/auth'
import { BASE_URL, VIEWER_BASE_URL } from '@/api/api'

class SampleAttachmentService extends AttachmentResource {
    async addAttachment(laboratoryId, caseId, sampleId, attachmentTypeId, file, thumbnailBytes) {
        let thumbnail

        if (thumbnailBytes) {
            thumbnail = this.base64ToBlob(thumbnailBytes, file.name)
        } else {
            let generatedThumbnailBytes = await this.thumbnailifyFromBlob(file)
            thumbnail = this.base64ToBlob(generatedThumbnailBytes, file.name)
        }

        const formData = new FormData()
        formData.append('file', file, file.name)
        formData.append('atyId', attachmentTypeId)

        if (thumbnail) {
            formData.append('thumbnail', thumbnail, file.name)
        }

        const response = await this.axios.post(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments`,
            formData, { headers: { 'content-type': 'multipart/form-data' } })

        return response.data
    }

    async getFile(laboratoryId, caseId, sampleId, attachmentId) {
        return this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}`, {
            headers: { 'content-type': 'multipart/form-data' },
            responseType: 'blob'
        }).then(response => response.data)
    }

    getUrlFile(laboratoryId, caseId, sampleId, attachmentId) {
        return `${BASE_URL}/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}?authenticationToken=${auth.token}`
    }

    async getAttachments(laboratoryId, caseId, sampleId, attachmentTypeId) {
        const params = { attachmentTypeId }
        const response = await this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments`, { params })
        return response.data
    }

    async getAttachment(laboratoryId, caseId, sampleId, attachmentId) {
        const response = await this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}/details`)
        return response.data
    }

    getUrlForAttachment(laboratoryId, caseId, sampleId, attachmentId, originalAttachment = false) {
        return `${BASE_URL}/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}?originalAttachment=${originalAttachment}&authenticationToken=${auth.token}`
    }

    getUrlForAttachmentFiltered(laboratoryId, caseId, sampleId, attachmentId) {
        return `${VIEWER_BASE_URL}/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}?authenticationToken=${auth.token}`
    }

    getUrlForAttachmentThumbnail(laboratoryId, caseId, sampleId, attachmentId) {
        return `${BASE_URL}/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}?authenticationToken=${auth.token}`
    }

    async getAttachmentsForAttachmentType(laboratoryId, caseId, sampleId, attachmentTypeId) {
        const params = { attachmentTypeId }
        return this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments`, { params }).then(response => response.data)
    }

    async getAllMacroImageAttachments(laboratoryId, caseId, sampleId) {
        const attachmentType = await attachmentTypeService.getAttachmentType('SAMPLE', 'attachment.type.macroscopicimage.code')
        return this.getAttachmentsForAttachmentType(laboratoryId, caseId, sampleId, attachmentType.atyId)
    }

    async getAllMacroVideoAttachments(laboratoryId, caseId, sampleId) {
        const attachmentType = await attachmentTypeService.getAttachmentType('SAMPLE', 'attachment.type.macroexaminationvideo.code')
        return this.getAttachmentsForAttachmentType(laboratoryId, caseId, sampleId, attachmentType.atyId)
    }

    async deleteAttachment(laboratoryId, caseId, sampleId, attachmentId) {
        const response = await this.axios.delete(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}`)
        return response.data
    }

    async patchAttachment(laboratoryId, caseId, sampleId, attachmentId, request) {
        const response = await this.axios.patch(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}`, request)
        return response.data
    }

    async loadSampleAnnotations(laboratoryId, caseId, sampleId, attachmentId) {
        return this.axios.get(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}/annotations`)
            .then(response => response.data)
    }

    async createSampleAnnotations(laboratoryId, caseId, sampleId, attachmentId, annotations, imageBytes, thumbnailBytes, attachmentFilename) {
        let annotationsBlob = new Blob([annotations], { 'type': 'application/json' })
        let thumbnail
        let image

        if (thumbnailBytes) {
            thumbnail = this.base64ToBlob(thumbnailBytes, 'thumbnail')
        }
        if (imageBytes) {
            image = this.base64ToBlob(imageBytes, 'image')
        }

        const formData = new FormData()
        formData.append('annotationFile', annotationsBlob, 'thumbnail')

        if (thumbnail) {
            formData.append('thumbnail', thumbnail)
        }
        if (image) {
            formData.append('image', image, attachmentFilename)
        }

        const params = { attachmentFilename }

        return this.axios.post(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}/annotations`,
            formData, { params, headers: { 'content-type': 'multipart/form-data' } })
            .then(response => response.data)
    }

    async updateSampleAnnotations(laboratoryId, caseId, sampleId, attachmentId, annotationId, annotations, imageBytes, thumbnailBytes, attachmentFilename) {
        let annotationsBlob = new Blob([annotations], { 'type': 'application/json' })
        let thumbnail
        let image

        if (thumbnailBytes) {
            thumbnail = this.base64ToBlob(thumbnailBytes, 'thumbnail')
        }
        if (imageBytes) {
            image = this.base64ToBlob(imageBytes, 'image')
        }

        const formData = new FormData()
        formData.append('annotationFile', annotationsBlob, 'thumbnail')

        if (thumbnail) {
            formData.append('thumbnail', thumbnail)
        }
        if (image) {
            formData.append('image', image, attachmentFilename)
        }

        const params = { attachmentFilename }

        return this.axios.put(`/common/laboratories/${laboratoryId}/cases/${caseId}/samples/${sampleId}/attachments/${attachmentId}/annotations/${annotationId}`,
            formData, { params, headers: { 'content-type': 'multipart/form-data' } })
            .then(response => response.data)
    }

    async updateMeasureScale(laboratoryId, caseId, sampleId, attachmentId, scale) {
        return this.patchAttachment(laboratoryId, caseId, sampleId, attachmentId, { scale })
    }
}

export default new SampleAttachmentService()
